import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { FaComments } from "react-icons/fa";
import "../styles/Read_More.css";

const ReadMore = () => {
  const { id } = useParams();
  const [talkshow, setTalkshow] = useState(null);
  const [categoryName, setCategoryName] = useState("Loading...");
  const [commentInput, setCommentInput] = useState("");
  const [commentVisible, setCommentVisible] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [currentUser, setCurrentUser] = useState({
    username: "Anonymous",
    id: null,
  });
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const commentSectionRef = useRef(null);

  // Load base URL from environment
  const baseUrl =
    (process.env.REACT_APP_BACKEND_API || "http://127.0.0.1:8000/api").replace(
      /\/$/,
      ""
    );

  // Load user info
  useEffect(() => {
    const username = localStorage.getItem("username") || "Anonymous";
    const userId = localStorage.getItem("userId") || null;
    const userRole = localStorage.getItem("user_role") || null;

    setIsUserAdmin(userRole === "Admin");
    setCurrentUser({ username, id: userId });
  }, []);

  // Fetch comments
  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${baseUrl}/talkshow-web/comments/${id}/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setComments(Array.isArray(data) ? data : []);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setError("Failed to load comments");
    } finally {
      setLoadingComments(false);
    }
  };

  // Submit new comment
  const submitComment = async () => {
    const trimmed = commentInput.trim();
    if (!trimmed) {
      setError("Please enter a comment.");
      return;
    }

    const user = localStorage.getItem("userId");
    const token = localStorage.getItem("access_token");
    const isAuthenticated = user && !isNaN(parseInt(user));

    const commentData = {
      show: parseInt(id),
      user: isAuthenticated ? parseInt(user) : null,
      comment: trimmed,
    };

    setSubmittingComment(true);
    setError(null);

    try {
      const response = await fetch(`${baseUrl}/talkshow-web/comment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(commentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Backend error:", errorData);
        throw new Error(errorData.message || "Failed to submit comment");
      }

      setCommentInput("");
      setCommentVisible(false);
      await fetchComments();
    } catch (error) {
      console.error("Submission error:", error);
      setError(error.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete a comment (admin only)
  const deleteComment = async (commentId) => {
    if (!isUserAdmin) {
      setError("Access denied. Only administrators can delete comments.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${baseUrl}/talkshow-web/comment-delete/${commentId}/`,
        {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (!response.ok) {
        if (response.status === 403)
          throw new Error("Access denied. Only administrators can delete comments.");
        if (response.status === 401)
          throw new Error("Authentication required. Please log in as an administrator.");
        if (response.status === 404) throw new Error("Comment not found.");
        throw new Error("Failed to delete comment. Please try again.");
      }

      setError(null);
      await fetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      setError(error.message);
    }
  };

  // Fetch talkshow data
  useEffect(() => {
    fetch(`${baseUrl}/shows/${id}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`Show not found: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setTalkshow(data);

        if (data.category) {
          fetch(`${baseUrl}/categories/${data.category}/`)
            .then((res) => res.json())
            .then((catData) => setCategoryName(catData.name))
            .catch(() => setCategoryName("Uncategorized"));
        } else {
          setCategoryName("Uncategorized");
        }
      })
      .catch(() => {
        setError(`Show not found or unavailable (ID: ${id})`);
      });

    fetchComments();
  }, [id]);

  if (!talkshow) return <div className="loading">Loading...</div>;

  const toggleExpand = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const scrollToComments = () => {
    commentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="readmore-container">
      <main className="readmore-content">
        {error && <div className="error-message">{error}</div>}

        <h1>{talkshow.title}</h1>
        
        <div className="readmore-meta-section">
          {categoryName && <span className="category-badge">{categoryName}</span>}
          <div className="meta-info">
            <span><strong> {talkshow.show_date} </strong></span>
            {/* <span><strong>Start Time:</strong> {talkshow.start_time}</span> */}
          </div>
        </div>
        
        <p className="description-text">{talkshow.description}</p>

        <section className="subtopics-section">
          {/* <h2>Deep Dive into the Topic</h2> */}
          {talkshow.subtopics.map((subtopic, index) => {
            const capitalizedHeader = subtopic.header_name 
              ? subtopic.header_name.charAt(0).toUpperCase() + subtopic.header_name.slice(1)
              : '';
            
            return (
              <div key={index} className="subtopic-item">
                <h3>{capitalizedHeader}</h3>
                <div
                  className="subtopic-html"
                  dangerouslySetInnerHTML={{
                    __html: subtopic.content,
                  }}
                />
                {subtopic.image_urls?.length > 0 && (
                  <div className="subtopic-images1">
                    {subtopic.image_urls.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Subtopic image ${i + 1}`}
                        className="subtopic-image"
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        <section className="profile-section">
          <h3>Co-Hosts</h3>
          <div className="profile-list">
            {talkshow.co_hosts.map((host) => (
              <div key={host.id} className="profile-card">
                <img
                  src={host.profileImgUrl}
                  alt={host.userName}
                  onError={(e) => (e.target.src = "/default-profile.png")}
                />
                <div>{host.userName}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="profile-section">
          <h3>Members</h3>
          <div className="profile-list">
            {talkshow.members.map((member) => (
              <div key={member.id} className="profile-card">
                {member.profileImgUrl ? (
                  <img
                    src={member.profileImgUrl}
                    alt={member.userName}
                    onError={(e) => (e.target.src = "/default-profile.png")}
                  />
                ) : (
                  <div className="no-image" />
                )}
                <div>{member.userName}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="comment-section" ref={commentSectionRef}>
          <h2>Share your ideas and thoughts</h2>
          {currentUser && (
            <p className="logged-in-user">
              <strong>{currentUser.username}</strong>
              {isUserAdmin && <span className="admin-badge"> (Admin)</span>}
            </p>
          )}
          <button
            className="add-comment-btn"
            onClick={() => setCommentVisible(!commentVisible)}
            disabled={submittingComment}
          >
            {commentVisible ? "Cancel" : "Add Comment"}
          </button>

          {commentVisible && (
            <div className="comment-form">
              <textarea
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                placeholder="Write your comment here..."
                rows={5}
              />
              <button onClick={submitComment} disabled={submittingComment}>
                {submittingComment ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}

          {loadingComments ? (
            <p>Loading comments...</p>
          ) : (
            <div className="comment-list">
              {comments.length === 0 ? (
                <p>No comments yet.</p>
              ) : (
                <>
                  <h3>Comments ({comments.length})</h3>
                  <ul>
                    {comments.map((comment) => (
                      <li key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <strong>{comment.username}</strong>
                          {isUserAdmin && (
                            <button
                              className="delete-comment-btn"
                              onClick={() => deleteComment(comment.id)}
                              title="Delete comment (Admin only)"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <p>{comment.comment}</p>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Floating Comment Bubble */}
      <div className="comment-bubble" onClick={scrollToComments}>
        <FaComments className="comment-bubble-icon" />
        <span className="comment-bubble-text">Comments</span>
      </div>
    </div>
  );
};

export default ReadMore;
